import PQueue, { type Queue, type QueueAddOptions } from "p-queue";
import pRetry, { type Options as RetryOptions } from "p-retry";
import { saveSlow } from "@/stores/SaveSlow.ts";
import { FetchNetworkError, FetchTimeout } from "@/util/fetch.tsx";
import { isNotEmpty } from "@/util/value.ts";

type RunFunction<T = unknown> = () => Promise<T>;

interface QueueOptions extends QueueAddOptions {
  debugInfo?: unknown;
}

interface QueueElement extends QueueOptions {
  run: RunFunction;
}

class QueueClass implements Queue<RunFunction, QueueOptions> {
  private readonly queue: QueueElement[] = [];

  enqueue(run: RunFunction, options: Partial<QueueOptions> = {}) {
    if (options.debugInfo == null) {
      this.queue.push({ run });
    } else {
      this.queue.push({ run, debugInfo: options.debugInfo });
    }

    void this.checkQueueSize();
  }

  dequeue() {
    const item = this.queue.shift();

    return item?.run;
  }

  get size() {
    return this.queue.length;
  }

  filter() {
    return this.queue.map((element) => element.run);
  }

  setPriority() {
    throw new Error("Not implemented");
  }

  private async checkQueueSize() {
    if (this.size >= 20) {
      const debugArray = this.queue
        .map((element) => element.debugInfo)
        .filter(isNotEmpty);

      await saveSlow(debugArray);
    }
  }
}

interface QueueHandle {
  add: <T>(fn: () => Promise<T>, debugInfo?: unknown) => Promise<T>;
}

export function createQueue(retries = 0): QueueHandle {
  const queue = new PQueue<QueueClass, QueueOptions>({
    concurrency: 1,
    queueClass: QueueClass,
  });

  if (retries > 0) {
    const retryOptions: RetryOptions = {
      retries,
      shouldRetry: ({ error }) =>
        error instanceof FetchTimeout || error instanceof FetchNetworkError,
    };

    return {
      add: (fn, debugInfo) =>
        queue.add(() => pRetry(fn, retryOptions), { debugInfo }),
    };
  }

  return {
    add: (fn, debugInfo) => queue.add(fn, { debugInfo }),
  };
}
